// Utils
import { parseAllowedRemoteHosts } from "src/utils/emr/allowedRemoteHostsHelper";
import {
  DEFAULT_POLL_INTERVAL_MINUTES,
  DEFAULT_WORKLIST_TIMING,
  WorklistTimingValues,
  buildWorklistTiming,
  worklistTimingFromConfig,
} from "src/utils/emr/worklistTimingHelper";

// Types
import {
  EMRConnectionType,
  EMRDicomStoreProfile,
  EMRExamType,
  EMRHubConnectionDirection,
  EMRHubConnectionSourceKind,
  EMRWorklistMode,
} from "src/services/emr/types";

export const DICOM_AE_TITLE_MAX_LENGTH = 16;
export const ASSOCIATION_TIMEOUT_SECONDS_MIN = 5;
export const ASSOCIATION_TIMEOUT_SECONDS_MAX = 300;

const DICOM_PORT_MIN = 1;
const DICOM_PORT_MAX = 65535;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export type ConnectionFormValues = {
  path: string;
  aeTitle: string;
  port: string;
  allowedCallingAeTitle: string;
  allowedRemoteHosts: string;
  localAeTitle: string;
  remoteAeTitle: string;
  host: string;
  remotePort: string;
  worklistMode: EMRWorklistMode;
  scheduledStationAeTitle: string;
  timing: WorklistTimingValues;
  pollIntervalMinutes: number;
  examTypes: Array<EMRExamType>;
  profile: EMRDicomStoreProfile;
  associationTimeoutSeconds: string;
  exportFrom: string;
};

export const EMPTY_CONNECTION_FORM_VALUES: ConnectionFormValues = {
  path: "",
  aeTitle: "",
  port: "",
  allowedCallingAeTitle: "FORUM",
  allowedRemoteHosts: "",
  localAeTitle: "",
  remoteAeTitle: "",
  host: "",
  remotePort: "",
  worklistMode: "DicomWorklist",
  scheduledStationAeTitle: "",
  timing: DEFAULT_WORKLIST_TIMING,
  pollIntervalMinutes: DEFAULT_POLL_INTERVAL_MINUTES,
  examTypes: [],
  profile: "Generic",
  associationTimeoutSeconds: "30",
  exportFrom: "",
};

export const FIXED_DIRECTIONS: Partial<
  Record<EMRHubConnectionSourceKind, EMRHubConnectionDirection>
> = {
  Worklist: "Inbound",
  DicomStore: "Outbound",
};

export const connectionFormValuesFromConfig = (
  connection: EMRConnectionType
): ConnectionFormValues => {
  const config = connection.config ?? {};
  const isListener = connection.sourceKind === "DicomListener";

  return {
    ...EMPTY_CONNECTION_FORM_VALUES,
    path: config.path ?? "",
    aeTitle: config.aeTitle ?? "",
    port: isListener ? String(config.port ?? "") : "",
    allowedCallingAeTitle:
      config.allowedCallingAeTitles?.[0] ??
      EMPTY_CONNECTION_FORM_VALUES.allowedCallingAeTitle,
    allowedRemoteHosts: config.allowedRemoteHosts?.join(", ") ?? "",
    localAeTitle: config.localAeTitle ?? "",
    remoteAeTitle: config.remoteAeTitle ?? "",
    host: config.host ?? "",
    remotePort: isListener ? "" : String(config.port ?? ""),
    worklistMode: config.mode ?? EMPTY_CONNECTION_FORM_VALUES.worklistMode,
    scheduledStationAeTitle: config.scheduledStationAeTitle ?? "",
    timing: worklistTimingFromConfig(config),
    pollIntervalMinutes:
      config.pollIntervalMinutes ?? DEFAULT_POLL_INTERVAL_MINUTES,
    examTypes: config.examTypes ?? [],
    profile: config.profile ?? EMPTY_CONNECTION_FORM_VALUES.profile,
    associationTimeoutSeconds: String(
      config.associationTimeoutSeconds ??
        EMPTY_CONNECTION_FORM_VALUES.associationTimeoutSeconds
    ),
    exportFrom: config.exportFrom?.slice(0, 10) ?? "",
  };
};

export const isValidExportFrom = (text: string) => {
  if (!text.trim()) return true;
  if (!DATE_ONLY.test(text.trim())) return false;

  const date = new Date(`${text.trim()}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === text.trim()
  );
};

const parsePort = (text: string) => {
  const port = Number(text);
  return Number.isInteger(port) &&
    text.trim() !== "" &&
    port >= DICOM_PORT_MIN &&
    port <= DICOM_PORT_MAX
    ? port
    : null;
};

const parseIntegerInRange = (text: string, min: number, max: number) => {
  const value = Number(text);
  return Number.isInteger(value) &&
    text.trim() !== "" &&
    value >= min &&
    value <= max
    ? value
    : null;
};

const buildRemoteArchive = (values: ConnectionFormValues) => {
  const localAeTitle = values.localAeTitle.trim();
  const remoteAeTitle = values.remoteAeTitle.trim();
  const host = values.host.trim();
  const port = parsePort(values.remotePort);

  if (!localAeTitle || !remoteAeTitle || !host || port === null) return null;

  return { localAeTitle, remoteAeTitle, host, port };
};

const examTypesOf = (values: ConnectionFormValues) =>
  values.examTypes.length > 0 ? { examTypes: values.examTypes } : {};

export const buildConnectionConfig = (
  sourceKind: EMRHubConnectionSourceKind,
  values: ConnectionFormValues
): Record<string, unknown> | null => {
  if (sourceKind === "SharedFolder") {
    const path = values.path.trim();
    return path ? { path } : null;
  }

  if (sourceKind === "DicomListener") {
    const aeTitle = values.aeTitle.trim();
    const port = parsePort(values.port);
    const callingAeTitle = values.allowedCallingAeTitle.trim();
    const remoteHosts = parseAllowedRemoteHosts(values.allowedRemoteHosts);

    if (
      !aeTitle ||
      port === null ||
      !callingAeTitle ||
      remoteHosts.invalid.length > 0
    ) {
      return null;
    }

    return {
      aeTitle,
      port,
      allowedCallingAeTitles: [callingAeTitle],
      ...(remoteHosts.hosts.length > 0 && {
        allowedRemoteHosts: remoteHosts.hosts,
      }),
      ...examTypesOf(values),
    };
  }

  if (sourceKind === "DicomQueryRetrieve") {
    const archive = buildRemoteArchive(values);
    return (
      archive && {
        ...archive,
        pollIntervalMinutes: values.pollIntervalMinutes,
        ...examTypesOf(values),
      }
    );
  }

  if (sourceKind === "Worklist") {
    const schedule = buildWorklistTiming(values.timing);
    if (!schedule) return null;

    if (values.worklistMode === "CsvFolder") {
      const path = values.path.trim();
      return path ? { mode: "CsvFolder", path, ...schedule } : null;
    }

    const archive = buildRemoteArchive(values);
    if (!archive) return null;

    const scheduledStationAeTitle = values.scheduledStationAeTitle.trim();

    return {
      mode: "DicomWorklist",
      ...archive,
      ...(scheduledStationAeTitle && { scheduledStationAeTitle }),
      ...schedule,
      ...examTypesOf(values),
    };
  }

  if (sourceKind === "DicomStore") {
    const archive = buildRemoteArchive(values);
    const associationTimeoutSeconds = parseIntegerInRange(
      values.associationTimeoutSeconds,
      ASSOCIATION_TIMEOUT_SECONDS_MIN,
      ASSOCIATION_TIMEOUT_SECONDS_MAX
    );
    const exportFrom = values.exportFrom.trim();

    if (
      !archive ||
      associationTimeoutSeconds === null ||
      !isValidExportFrom(exportFrom)
    ) {
      return null;
    }

    return {
      ...archive,
      reportTypes: ["Dicom"],
      profile: values.profile,
      associationTimeoutSeconds,
      ...(exportFrom && { exportFrom: `${exportFrom}T00:00:00Z` }),
    };
  }

  return null;
};

const FORM_OWNED_KEYS = [
  "path",
  "aeTitle",
  "port",
  "allowedCallingAeTitles",
  "allowedRemoteHosts",
  "localAeTitle",
  "remoteAeTitle",
  "host",
  "mode",
  "scheduledStationAeTitle",
  "importTimes",
  "lookaheadDays",
  "profile",
  "associationTimeoutSeconds",
  "exportFrom",
  "examTypes",
];

export const mergeWithStoredConfig = (
  sourceKind: EMRHubConnectionSourceKind,
  stored: EMRConnectionType["config"] | undefined,
  built: Record<string, unknown>
): Record<string, unknown> => {
  const owned = new Set([
    ...FORM_OWNED_KEYS,
    ...(sourceKind === "Worklist" || sourceKind === "DicomQueryRetrieve"
      ? ["pollIntervalMinutes"]
      : []),
    ...("examTypes" in built ? ["modalities", "acceptedSopClasses"] : []),
  ]);
  const kept = Object.fromEntries(
    Object.entries(stored ?? {}).filter(([key]) => !owned.has(key))
  );

  return { ...kept, ...built };
};
